import React, { useEffect, useState } from 'react';
import { BeamSweep, ExtractionChip, ChipList } from 'features/Onboarding/components/ScanRevealPrimitives';
import { CHIP_STAGGER_MS } from '../constants';
import { VignetteProps } from '../types';

const ScanMini: React.FC<VignetteProps> = ({ targetRect, cardRect, reduced }) => {
  const [sweeping, setSweeping] = useState(false);
  const [chips, setChips] = useState(reduced ? 2 : 0);

  useEffect(() => {
    if (reduced) return undefined;
    const timers = [
      window.setTimeout(() => setSweeping(true), 300),
      window.setTimeout(() => setChips(1), 1400),
      window.setTimeout(() => setChips(2), 1400 + CHIP_STAGGER_MS),
    ];
    return () => timers.forEach((t) => window.clearTimeout(t));
  }, [reduced]);

  if (!targetRect) return null;
  // Centered on the target (not the viewport) so the panel sits close to
  // whichever add button is live - mobile's centered nav pill or desktop
  // header's add button, which floats far from the viewport midpoint.
  // Clamped so the 210px panel stays >= 12px from both viewport edges.
  // Wide enough for the extraction chips (label + value pills measure up to
  // ~280px) so they never spill past the panel's right edge.
  const panelWidth = Math.min(300, window.innerWidth - 24);
  const targetCenter = targetRect.left + targetRect.width / 2;
  // Side-aware vertical placement: the panel is ~130px tall including its
  // margin. When the target sits too close to the top of the viewport (e.g.
  // desktop's header add-button, targetRect.top ~20px) there isn't enough
  // room to anchor above without pushing the panel off-screen, so anchor
  // below the target instead. Mobile's centered nav pill has plenty of room
  // above and keeps the original placement.
  const below = targetRect.top < 170;
  // Item D: in the above branch, anchor above whichever sits higher of the
  // target and the step card - on mobile the card is centered on the target
  // and can extend above it, so anchoring purely off the target left the
  // panel hidden behind the card. Falls back to the target alone when the
  // card hasn't measured yet.
  const aboveAnchor = Math.min(targetRect.top, cardRect ? cardRect.top : Infinity);
  const verticalStyle = below
    ? { top: targetRect.bottom + 18 }
    : { bottom: window.innerHeight - aboveAnchor + 18 };
  // In the below branch the step card also lands under the target (300px
  // wide, centered on it) and paints over the panel, so side-step the panel
  // horizontally clear of the card - right of it when there's room, else
  // mirrored to the left. The above branch keeps target-centering.
  const centered = Math.max(targetCenter - panelWidth / 2, 12);
  const rightOfCard = targetCenter + 150 + 12;
  const leftOfCard = targetCenter - 150 - 12 - panelWidth;
  const sideStepped = rightOfCard + panelWidth + 12 <= window.innerWidth
    ? rightOfCard
    : Math.max(leftOfCard, 12);
  const left = Math.min(
    below ? sideStepped : centered,
    window.innerWidth - panelWidth - 12,
  );
  return (
    <div
      style={{
        position: 'fixed',
        left,
        ...verticalStyle,
        width: panelWidth,
        pointerEvents: 'none',
        fontFamily: 'Grift, sans-serif',
      }}
      aria-hidden="true"
    >
      <div
        style={{
          position: 'relative',
          height: 84,
          borderRadius: 12,
          background: '#fff',
          border: '1px solid #CDAAF4',
          boxShadow: '0 8px 22px rgba(171,114,237,.4)',
          overflow: 'hidden',
          marginBottom: 8,
          padding: 10,
        }}
      >
        <div
          style={{
            height: 5, borderRadius: 3, background: '#E6E1EB', marginBottom: 6, width: '70%',
          }}
        />
        <div
          style={{
            height: 5, borderRadius: 3, background: '#E6E1EB', marginBottom: 6, width: '55%',
          }}
        />
        <div
          style={{
            height: 5, borderRadius: 3, background: '#E6E1EB', width: '62%',
          }}
        />
        <BeamSweep
          sweeping={sweeping}
          clipRadius={12}
          heightPercent={30}
          topStartPercent={-14}
          durationMs={1100}
          keyframesName="shepherdScanMiniSweep"
        />
      </div>
      <ChipList>
        <ExtractionChip icon={null} label="Renews" value="24 Jul" visible={chips >= 1} />
        <ExtractionChip icon={null} label="Category" value="Vehicle" visible={chips >= 2} />
      </ChipList>
    </div>
  );
};

export default ScanMini;
